import type { ComponentPropsWithoutRef } from "react";
import { forwardRef } from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "@renewmap/ui/utils/class-name";
import { CheckIcon } from "@radix-ui/react-icons";
import { cva, type VariantProps } from "class-variance-authority";

const checkboxVariants = cva(
  "border-none focus-visible:ring-ring peer shrink-0 rounded-sm border focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 hover:bg-muted/50 data-[state=checked]:hover:bg-primary/90 transition-colors",
  {
    variants: {
      variant: {
        default:
          "bg-primary/10 hover:bg-primary/20 data-[state=checked]:bg-primary/10 data-[state=checked]:text-blue-900 data-[state=checked]:hover:bg-primary/20",
        secondary:
          "bg-secondary/50 hover:bg-secondary data-[state=checked]:bg-secondary/50 data-[state=checked]:text-secondary-foreground data-[state=checked]:hover:bg-secondary",
        primary:
          "bg-primary/10 hover:bg-primary/20 data-[state=checked]:bg-primary/90 data-[state=checked]:text-primary-foreground",
      },
      size: {
        default: "h-4 w-4",
        lg: "h-5 w-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface CheckboxProps
  extends ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {}

const Checkbox = forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant, size, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(checkboxVariants({ variant, size }), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      <CheckIcon className={cn("h-4 w-4", size === "lg" && "h-5 w-5")} />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox, checkboxVariants };
